import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { Company } from '@/types/company';
import * as integrationsService from '@/services/integrationsService';
import { clearProductTypeCache } from '@/services/integrationsService';
import { updateCompanyId } from '@/lib/auth-fetch';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import { useDataMode } from '@/contexts/DataModeContext';
import type { CompanyWithIntegrations } from '@/types/integrations';

interface CompanyContextType {
  currentCompany: Company | null;
  currentCompanyIntegrations: CompanyWithIntegrations | null;
  availableCompanies: Company[];
  hasCompanyAccess: boolean;
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => void;
  refreshCompanies: () => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = 'ebscheduler_current_company';

interface CompanyProviderProps {
  children: ReactNode;
}

/**
 * Maps a CompanyWithIntegrations (from integrations service) to the
 * simpler Company type used by the company selector dropdown.
 */
function mapToCompany(c: CompanyWithIntegrations): Company {
  // Derive instanceId from the planner integration's externalInstanceId if available
  const instanceId = c.plannerIntegration?.externalInstanceId ?? undefined;
  return {
    id: c.id,
    name: c.name,
    code: c.code,
    instanceId,
    status: c.status === 'active' ? 'active' : 'inactive',
  };
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const { getScreenMode } = useDataMode();
  const isDemo = getScreenMode('settings') === 'demo';
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [currentCompanyIntegrations, setCurrentCompanyIntegrations] = useState<CompanyWithIntegrations | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [companiesWithIntegrations, setCompaniesWithIntegrations] = useState<CompanyWithIntegrations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadCompaniesInFlightRef = useRef<Promise<void> | null>(null);

  // When mode changes (demo↔live), immediately invalidate the stale company
  // so that child effects (e.g. booking fetch) don't fire with the wrong
  // company ID before loadCompanies has a chance to run.
  const prevIsDemoRef = useRef(isDemo);
  if (prevIsDemoRef.current !== isDemo) {
    prevIsDemoRef.current = isDemo;
    setCurrentCompany(null);
    setIsLoading(true);
  }

  const performLoadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Load companies from the same source as the Integrations page
      const data = isDemo
        ? await mockIntegrationsService.getCompanies()
        : await integrationsService.getCompanies();

      setCompaniesWithIntegrations(data);
      const companies = data.map(mapToCompany);
      let activeCompanies = companies.filter(c => c.status === 'active');

      // Filter companies by current user's access (skip in demo mode).
      // If the user has 0 access records the list will be empty and the
      // company dropdown will be hidden automatically.
      let defaultCompanyId: string | null = null;
      if (!isDemo) {
        try {
          const rawAccess = await integrationsService.getMyCompanyAccess();
          // Handle both old (Guid[]) and new ({ companyId, isDefault }[]) response shapes
          const accessRecords = rawAccess.map(r =>
            typeof r === 'string'
              ? { companyId: r, isDefault: false }
              : { companyId: String(r.companyId ?? r), isDefault: Boolean(r.isDefault) }
          );
          const idSet = new Set(accessRecords.map(r => r.companyId.toLowerCase()));
          activeCompanies = activeCompanies.filter(c => idSet.has(String(c.id).toLowerCase()));
          const defaultRecord = accessRecords.find(r => r.isDefault);
          if (defaultRecord) {
            defaultCompanyId = defaultRecord.companyId;
          }
        } catch (accessErr) {
          console.error('Failed to load company access, showing all companies:', accessErr);
        }
      }

      setAvailableCompanies(activeCompanies);

      // Try to restore from local storage
      let companyToLoad: Company | null = null;
      const storedCompanyId = localStorage.getItem(STORAGE_KEY);
      if (storedCompanyId) {
        companyToLoad = activeCompanies.find((c) => String(c.id) === storedCompanyId) || null;
      }

      // If localStorage didn't resolve, use the server-side default
      if (!companyToLoad && defaultCompanyId) {
        companyToLoad = activeCompanies.find(
          (c) => String(c.id).toLowerCase() === defaultCompanyId.toLowerCase()
        ) || null;
      }

      // If no stored company or server default, use first available
      if (!companyToLoad && activeCompanies.length > 0) {
        companyToLoad = activeCompanies[0];
      }

      if (companyToLoad) {
        setCurrentCompany(companyToLoad);
        setCurrentCompanyIntegrations(data.find(c => c.id === companyToLoad.id) || null);
        localStorage.setItem(STORAGE_KEY, String(companyToLoad.id));
        // Keep the X-Company-Id header in sync so every API call
        // targets the correct company.
        updateCompanyId(companyToLoad.id);
      } else {
        setError('No companies available');
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [isDemo]);

  const loadCompanies = useCallback(() => {
    if (loadCompaniesInFlightRef.current) {
      return loadCompaniesInFlightRef.current;
    }

    const request = performLoadCompanies();
    loadCompaniesInFlightRef.current = request;
    void request.finally(() => {
      if (loadCompaniesInFlightRef.current === request) {
        loadCompaniesInFlightRef.current = null;
      }
    });
    return request;
  }, [performLoadCompanies]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Refresh the company list when user access is added/removed
  useEffect(() => {
    const handler = () => loadCompanies();
    window.addEventListener('company-access-changed', handler);
    return () => window.removeEventListener('company-access-changed', handler);
  }, [loadCompanies]);

  const switchCompany = useCallback(
    (companyId: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const company = availableCompanies.find((c) => String(c.id) === companyId);
        if (!company) {
          throw new Error('Company not found or access denied');
        }

        setCurrentCompany(company);
        setCurrentCompanyIntegrations(companiesWithIntegrations.find(c => c.id === companyId) || null);
        localStorage.setItem(STORAGE_KEY, companyId);

        // Keep the X-Company-Id header in sync so every subsequent
        // API call targets the newly-selected company.
        updateCompanyId(companyId);

        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
      } catch (err) {
        console.error('Failed to switch company:', err);
        setError('Failed to switch company');
      } finally {
        setIsLoading(false);
      }
    },
    [availableCompanies, companiesWithIntegrations]
  );

  const refreshCompanies = useCallback(() => {
    // Clear stale product-type cache so the next integration lookup
    // fetches fresh data (e.g. after a category migration is applied).
    clearProductTypeCache();
    loadCompanies();
  }, [loadCompanies]);

  const hasCompanyAccess = availableCompanies.length > 0;

  const value: CompanyContextType = {
    currentCompany,
    currentCompanyIntegrations,
    availableCompanies,
    hasCompanyAccess,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
